/*
 * ENG-4159 RTSP Assisted Setup — common-format catalog (client-embedded).
 *
 * When the user picks Brand="Other" the device registry has no template to
 * seed an RTSP path from, so we offer a small hardcoded set of the most common
 * vendor URL formats. The user selects one; the screen composes the candidate
 * `rtsp://` URL from the chosen {port, path} plus the entered IP/credentials
 * via `composeRtspUrl` (W1).
 *
 * This is a CLIENT constant, NOT a device-registry entity — no API/DR/proto
 * change (rule stay-in-scope workflow). Port/shape ported from prior-art
 * 11dde12b4 (`{ id, label, port, path, sub? }`). The entry set is the
 * engineer-chosen minimal catalog: three famous brands NOT in the device
 * registry (Dahua/Amcrest/Lorex, Hikvision/OEM, Reolink — a user reaches
 * "Other" precisely because their brand isn't listed) + three brand-agnostic
 * generic paths that are the most common in the device registry (so they also
 * catch unlisted OEM/whitelabel cameras that share them). Extend by adding
 * entries below — the shape is the only contract downstream code relies on
 * (rule think-in-systems global).
 */

/** A catalog vendor entry: a main stream path and an optional substream path. */
export interface CatalogEntry {
  /** Stable id used as the select option value. */
  id: string;
  /** Human-readable label shown in the format picker. */
  label: string;
  /** RTSP port for this format. */
  port: number;
  /** Primary (main / high-res) stream path. */
  path: string;
  /** Optional secondary (sub / low-res) stream path. */
  sub?: string;
  /**
   * IP-agnostic `rtsp://<ip>…` URL-template preview shown under the label in
   * the Brand="Other" format picker. Illustrative only — the real composed URL
   * substitutes the entered IP/credentials via `composeRtspUrl`.
   */
  preview: string;
}

/**
 * Common RTSP URL formats offered on the Brand="Other" path. Credential-
 * agnostic — credentials are folded into the composed URL separately. Each
 * entry maps 1:1 onto the structured `{ port, path }` fields consumed by
 * `composeRtspUrl`.
 */
export const COMMON_FORMAT_CATALOG: readonly CatalogEntry[] = [
  // --- Famous brands NOT in the device registry (a user reaches "Other"
  //     because their brand isn't listed), ordered by global install base. ---
  // Dahua main channel — one entry covers Dahua + Amcrest + most Lorex (all
  // Dahua-OEM). subtype=0 main / subtype=1 sub.
  {
    id: 'dahua',
    label: 'Dahua / Amcrest / Lorex',
    path: '/cam/realmonitor?channel=1&subtype=0',
    port: 554,
    preview: 'rtsp://<ip>:554/cam/realmonitor?channel=1&subtype=0'
  },
  // Hikvision (ISAPI) main channel — also LTS / Annke / many Hikvision OEMs.
  {
    id: 'hikvision',
    label: 'Hikvision / OEM',
    path: '/Streaming/Channels/101',
    port: 554,
    preview: 'rtsp://<ip>:554/Streaming/Channels/101'
  },
  // Reolink main stream (H.264). H.265/4K models use `h265Preview_01_main`.
  {
    id: 'reolink',
    label: 'Reolink',
    path: '/h264Preview_01_main',
    port: 554,
    preview: 'rtsp://<ip>:554/h264Preview_01_main'
  },
  // --- Generic, brand-agnostic paths (the most common in the device registry,
  //     so they also catch unlisted OEM/whitelabel cameras that share them). ---
  {
    id: 'generic-stream-main',
    label: 'Generic — /stream/main',
    path: '/stream/main',
    port: 554,
    preview: 'rtsp://<ip>:554/stream/main'
  },
  {
    id: 'generic-live-av0',
    label: 'Generic — /live/av0',
    path: '/live/av0',
    port: 554,
    preview: 'rtsp://<ip>:554/live/av0'
  },
  // Very short generic path — accepted by many cameras even when their docs
  // don't list it (engineer field observation).
  {
    id: 'generic-1',
    label: 'Generic — /1',
    path: '/1',
    port: 554,
    preview: 'rtsp://<ip>:554/1'
  }
];
